import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    LayoutDashboard, CalendarRange, BedDouble, Users, Sparkles, Receipt, Globe2,
    BarChart3, Building2, FileText, Settings, ChevronsLeft, ChevronsRight,
    UsersRound, ShieldCheck, CreditCard, LogOut, Layers, Server, Flag,
    LifeBuoy, Mail, Plug, Webhook, FileBarChart, FilePieChart, Cog, Wand2, Wallet, TrendingUp, Inbox,
    Award, Star, Languages, Tag, Rocket, AlertTriangle, Database, Ban, MessageSquare,
} from "lucide-react";

const HOTEL_NAV = [
    { section: "operations", items: [
        { to: "/dashboard", labelKey: "dashboard", icon: LayoutDashboard, mod: "dashboard" },
        { to: "/reservations", labelKey: "reservations", icon: CalendarRange, mod: "reservations" },
        { to: "/check-ins", labelKey: "online_checkin", icon: ShieldCheck, mod: "checkins", feature: "online_checkin" },
        { to: "/rooms", labelKey: "rooms", icon: BedDouble, mod: "rooms" },
        { to: "/room-types", label: "Kategoritë e dhomave", icon: BedDouble, mod: "rooms", roles: ["hotel_admin", "super_admin"] },
        { to: "/guests", labelKey: "guests", icon: Users, mod: "guests" },
        { to: "/housekeeping", labelKey: "housekeeping", icon: Sparkles, mod: "housekeeping" },
        { to: "/pos", labelKey: "pos", icon: Receipt, mod: "pos", feature: "pos" },
        { to: "/billing", labelKey: "billing", icon: Wallet, mod: "billing" },
        { to: "/settings/subscription", labelKey: "subscription_billing", label: "Subscription & Billing", icon: CreditCard, mod: "subscription_billing" },
        { to: "/inbox", labelKey: "inbox", icon: Inbox, mod: "inbox", feature: "email_messaging" },
        { to: "/loyalty", labelKey: "loyalty", icon: Award, mod: "loyalty", feature: "loyalty" },
        { to: "/surveys", labelKey: "surveys", icon: Star, mod: "surveys", feature: "guest_reviews" },
    ]},
    { section: "manage", items: [
        { to: "/channels", labelKey: "channels", icon: Globe2, mod: "channels", feature: "channel_manager" },
        { to: "/rate-plans", labelKey: "rate_plans", icon: Tag, mod: "rate_plans" },
        { to: "/restrictions", label: "Kufizime", icon: Ban, mod: "rate_plans" },
        { to: "/booking-engine", labelKey: "booking_engine", icon: Sparkles, mod: "settings", feature: "booking_engine" },
        { to: "/website-booking", labelKey: "website_booking", icon: Globe2, mod: "settings", feature: "website_widget" },
        { to: "/revenue", labelKey: "revenue", icon: TrendingUp, mod: "revenue", feature: "revenue_management" },
        { to: "/integrations", labelKey: "integrations", icon: Plug, mod: "integrations" },
        { to: "/reports", labelKey: "reports", icon: BarChart3, mod: "reports" },
        { to: "/properties", labelKey: "properties", icon: Building2, mod: "properties" },
        { to: "/staff", labelKey: "staff", icon: UsersRound, mod: "staff" },
        { to: "/wizard", labelKey: "setup_wizard", icon: Wand2, mod: "settings" },
        { to: "/audit", labelKey: "audit_logs", icon: FileText, mod: "audit" },
        { to: "/settings", labelKey: "settings", icon: Settings, mod: "settings" },
    ]},
];

const SUPER_NAV = [
    { section: "Overview", key: "overview", items: [
        { to: "/admin", labelKey: "overview_title", icon: LayoutDashboard, end: true },
        { to: "/admin/reports", labelKey: "reports_title", icon: FilePieChart },
    ]},
    { section: "Tenants", key: "tenants", items: [
        { to: "/admin/hotels", labelKey: "hotels_title", icon: Building2 },
        { to: "/admin/properties", labelKey: "properties_title", icon: BedDouble },
        { to: "/admin/users", labelKey: "users_title", icon: UsersRound },
        { to: "/admin/modules", labelKey: "modules_title", icon: Layers },
        { to: "/wizard", labelKey: "setup_wizard", icon: Wand2 },
    ]},
    { section: "Billing", key: "billing", items: [
        { to: "/admin/billing", labelKey: "billing_dashboard", icon: LayoutDashboard, end: true },
        { to: "/admin/billing/pricing", labelKey: "pricing_versions", icon: CreditCard },
        { to: "/admin/billing/modules", labelKey: "module_catalog", icon: Layers },
        { to: "/admin/billing/plans", labelKey: "plans_bundles", icon: Sparkles },
        { to: "/admin/billing/subscriptions", labelKey: "subscriptions_title", icon: ShieldCheck },
        { to: "/admin/billing/invoices", labelKey: "invoices_title", icon: FileBarChart },
        { to: "/admin/billing/audit", labelKey: "billing_audit", icon: FileText },
        { to: "/admin/dunning", label: "Dunning", icon: AlertTriangle },
        { to: "/admin/gateways", labelKey: "gateways_title", icon: Webhook },
    ]},
    { section: "Integrations", key: "integrations", items: [
        { to: "/admin/distribution", labelKey: "distribution_title", icon: Globe2 },
        { to: "/admin/otas", labelKey: "otas_title", icon: Globe2 },
        { to: "/admin/channel-manager", labelKey: "channel_manager_title", icon: Plug },
        { to: "/admin/booking-engine", labelKey: "booking_engine_title", icon: Sparkles },
        { to: "/admin/email-templates", labelKey: "email_templates_title", icon: Mail },
        { to: "/admin/demo-requests", labelKey: "demo_requests_title", icon: Mail },
        { to: "/admin/sales-leads", label: "Sales Leads", icon: MessageSquare },
        { to: "/admin/onboarding-analytics", label: "Analitika Onboarding", icon: TrendingUp },
    ]},
    { section: "Platform", key: "platform", items: [
        { to: "/admin/tickets", labelKey: "support_title", icon: LifeBuoy },
        { to: "/admin/system-logs", labelKey: "system_logs_title", icon: Server },
        { to: "/audit", labelKey: "audit_logs_title", icon: FileText },
        { to: "/admin/flags", labelKey: "feature_flags_title", icon: Flag },
        { to: "/admin/global-settings", labelKey: "global_settings_title", icon: Cog },
        { to: "/admin/languages", labelKey: "languages_title", icon: Languages },
        { to: "/admin/backups", label: "Kopjet e sigurisë", icon: Database },
        { to: "/admin/deploy", labelKey: "deploy_title", icon: Rocket },
    ]},
];

const MOD_BY_ROLE = {
    hotel_admin: null,
    property_manager: ["dashboard", "properties", "rooms", "reservations", "checkins", "guests", "housekeeping", "pos", "billing", "subscription_billing", "inbox", "loyalty", "surveys", "revenue", "integrations", "reports", "rate_plans", "channels"],
    reception: ["dashboard", "reservations", "checkins", "guests", "rooms", "inbox", "loyalty", "surveys"],
    housekeeping: ["dashboard", "housekeeping", "rooms"],
    pos_staff: ["dashboard", "pos"],
    finance_staff: ["dashboard", "reports", "billing", "subscription_billing"],
};

function filterHotelNav(role, features) {
    const allowed = MOD_BY_ROLE[role];
    return HOTEL_NAV.map((section) => ({
        ...section,
        items: section.items.filter((i) => {
            // Role gating
            if (allowed && !allowed.includes(i.mod)) return false;
            // Feature (paid module) gating — only hide if feature key is defined
            // and NOT active for this hotel. If features === null (Super Admin
            // or fetch failed), show everything for safety.
            if (i.feature && features && !features[i.feature]) return false;
            return true;
        }),
    })).filter((s) => s.items.length > 0);
}

export default function Sidebar({ collapsed, onToggle }) {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isSuper = user?.role === "super_admin";
    // Fetch effective module features for this hotel (paid module gating).
    // For Super Admin: skipped (they see everything). For hotel users: refreshes
    // on mount and every 60s so newly-enabled modules appear without a hard reload.
    const [features, setFeatures] = useState(null);
    useEffect(() => {
        if (isSuper) { setFeatures(null); return; }
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/auth/me/features");
                if (!cancelled) setFeatures(data.all ? null : data.features || {});
            } catch { /* silent — sidebar falls back to role-only gating */ }
        };
        load();
        const iv = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [isSuper]);
    const sections = isSuper ? SUPER_NAV : filterHotelNav(user?.role, features);

    // Poll Channex outbox status so Super Admins never miss an active incident
    // — a red dot badge appears next to the Distribution link the moment the
    // alert fires. Polls every 30s; silent on transient errors.
    const [distributionAlert, setDistributionAlert] = useState(null);
    useEffect(() => {
        if (!isSuper) return;
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/channex/outbox/stats");
                if (cancelled) return;
                setDistributionAlert({
                    active: !!data?.alert?.active,
                    escalated: !!data?.alert?.escalated_at,
                    rows: data?.outbox_rows ?? 0,
                });
            } catch { /* silent */ }
        };
        load();
        const iv = setInterval(load, 30_000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [isSuper]);

    // For super admin we look up via super_admin.<labelKey>; for hotel staff sidebar.<labelKey>
    const renderLabel = (item) => {
        if (!item.labelKey) return item.label;
        return isSuper ? t(`super_admin.${item.labelKey}`) : t(`sidebar.${item.labelKey}`);
    };
    const renderSection = (section) => {
        if (isSuper) return t(`super_admin.section_${section.key || section.section.toLowerCase()}`, section.section);
        return t(`sidebar.${section.section}`);
    };

    return (
        <aside
            data-testid="sidebar"
            className={`flex h-screen flex-col border-r border-border bg-card transition-[width] duration-150 ${
                collapsed ? "w-[62px]" : "w-[232px]"
            }`}
        >
            <div className={`flex ${collapsed ? "flex-col-reverse items-center gap-1 py-2" : "min-h-[72px] items-start justify-between gap-2 pt-2.5 pb-2"} border-b border-border px-3`}>
                {collapsed ? (
                    <img
                        src="/brand/akomoda-mark.png"
                        alt="Akomoda"
                        className="h-10 w-10 select-none object-contain"
                        draggable={false}
                    />
                ) : (
                    <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                        <img
                            src="/brand/akomoda-light.webp"
                            alt="Akomoda"
                            className="block w-full max-w-[200px] select-none object-contain dark:hidden"
                            draggable={false}
                        />
                        <img
                            src="/brand/akomoda-dark.webp"
                            alt="Akomoda"
                            className="hidden w-full max-w-[200px] select-none object-contain dark:block"
                            draggable={false}
                        />
                    </div>
                )}
                <button
                    data-testid="sidebar-toggle"
                    onClick={onToggle}
                    className={`rounded-md p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground ${collapsed ? "" : "ml-auto"}`}
                    title={collapsed ? "Expand" : "Collapse"}
                    aria-label={collapsed ? "Expand" : "Collapse"}
                >
                    {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={14} />}
                </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-2 py-3">
                {sections.map((section, sIdx) => (
                    <div key={section.section} className={sIdx > 0 ? "mt-3" : ""}>
                        {!collapsed && (
                            <div className="px-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                {renderSection(section)}
                            </div>
                        )}
                        <ul className="flex flex-col gap-0.5">
                            {section.items.map((item) => {
                                const { to, icon: Icon, end } = item;
                                const label = renderLabel(item);
                                const isDistribution = isSuper && to === "/admin/distribution";
                                const showBadge = isDistribution && distributionAlert?.active;
                                const badgeTitle = showBadge
                                    ? `${distributionAlert.escalated ? "Escalated" : "Active"} incident · ${distributionAlert.rows} rows`
                                    : undefined;
                                return (
                                <li key={to}>
                                    <NavLink
                                        to={to}
                                        end={end}
                                        data-testid={`sidebar-link-${(item.labelKey || item.label).toLowerCase().replace(/\s+/g, "-")}`}
                                        className={({ isActive }) =>
                                            `group flex items-center gap-2.5 rounded-md ${
                                                collapsed ? "justify-center px-1.5 py-2" : "px-2 py-1.5"
                                            } text-[13px] font-medium transition-colors ${
                                                isActive
                                                    ? "bg-accent text-accent-foreground"
                                                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                                            }`
                                        }
                                        title={collapsed ? (badgeTitle ? `${label} — ${badgeTitle}` : label) : badgeTitle}
                                    >
                                        <span className="relative shrink-0">
                                            <Icon size={collapsed ? 20 : 16} />
                                            {showBadge && (
                                                <span
                                                    data-testid="sidebar-distribution-alert-dot"
                                                    aria-label={badgeTitle}
                                                    className={`absolute -right-1 -top-1 inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-card ${
                                                        distributionAlert.escalated
                                                            ? "bg-red-500 animate-pulse"
                                                            : "bg-amber-500 animate-pulse"
                                                    }`}
                                                />
                                            )}
                                        </span>
                                        {!collapsed && (
                                            <span className="truncate flex-1">{label}</span>
                                        )}
                                        {!collapsed && showBadge && (
                                            <span
                                                data-testid="sidebar-distribution-alert-count"
                                                className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide ${
                                                    distributionAlert.escalated
                                                        ? "bg-red-100 text-red-700"
                                                        : "bg-amber-100 text-amber-800"
                                                }`}
                                            >
                                                {distributionAlert.escalated ? "ESC" : "!"}
                                            </span>
                                        )}
                                    </NavLink>
                                </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className="border-t border-border p-2">
                <div className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${collapsed ? "justify-center" : ""}`}>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
                        {user?.name?.[0] || "?"}
                    </div>
                    {!collapsed && (
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-[13px] font-medium" data-testid="sidebar-user-name">{user?.name}</div>
                            <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                                {user?.role?.replace("_", " ")}
                            </div>
                        </div>
                    )}
                    <button
                        data-testid="sidebar-logout"
                        onClick={async () => { await logout(); navigate("/login"); }}
                        className="rounded-md p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        title="Sign out"
                    >
                        <LogOut size={14} />
                    </button>
                </div>
            </div>
        </aside>
    );
}
